// apps/account-portal/src/components/CheckoutModal.tsx
import React, { useState } from 'react';
import { subscriptionApi } from '../services/api';
import { useTranslation } from 'react-i18next';
import { useSubscriptionStore } from '../stores/subscriptionStore';
import { SystemType, Plan } from '@redpanda/types';
import { toast } from 'react-hot-toast';
import { LoadingButton, DialogShell, DialogFooter, dialogButtonClass } from '@redpanda/ui';
import CheckmarkIcon from './CheckmarkIcon';

interface CheckoutModalProps {
  isOpen: boolean;
  onClose: () => void;
  plan: Plan | null;
  system: SystemType;
  onPaymentSuccess?: () => void;
}

const CheckoutModal: React.FC<CheckoutModalProps> = ({ isOpen, onClose, plan, system, onPaymentSuccess }) => {
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();
  const subscriptionStore = useSubscriptionStore();

  if (!isOpen || !plan) return null;

  // planId 格式為 {system}_{type}，例如 crm_free、accounting_basic
  // 用最後一個底線分割，自動支援所有系統，新增系統無需修改此處
  const getPlanFeatures = (planId: string): string[] => {
    const sep = planId.lastIndexOf('_');
    if (sep === -1) return [];
    const systemKey = planId.substring(0, sep);
    const planType = planId.substring(sep + 1);
    const features = t(`systems.${systemKey}.plan_features.${planType}`, { ns: 'staticContent', returnObjects: true });
    return Array.isArray(features) ? (features as string[]) : [];
  };

  const getPlanName = (planId: string): string => {
    const sep = planId.lastIndexOf('_');
    if (sep === -1) return plan?.name || planId;
    const systemKey = planId.substring(0, sep);
    const planType = planId.substring(sep + 1);
    return t(`systems.${systemKey}.plans.${planType}.name`, { ns: 'staticContent' });
  };

  const currency = t('pricing.currency', { ns: 'staticContent' }) || 'NT$';

  const handleConfirmPayment = async () => {
    if (!plan || !system) {
      toast.error(t('common.messages.validationFailed'));
      return;
    }

    setLoading(true);

    try {
      // 先取得現有訂閱（快取優先，不重複呼叫）
      const existingSubscription = await subscriptionStore.fetchSubscription(system).catch(() => null);

      // 開始／到期日、狀態都由後端決定（金流串接前的過渡做法，見 subscription-service 的 subscriptionRoutes）
      if (existingSubscription?.id) {
        // 換方案：付費↔免費都是改同一筆訂閱（保留歷史記錄）
        await subscriptionApi.patch(`/subscriptions/${existingSubscription.id}`, { planId: plan.id });
      } else if (!plan.id.includes('_free')) {
        // 首次訂閱付費版
        await subscriptionApi.post('/subscriptions', { system, planId: plan.id });
      }

      // 成功處理 - 採用 CRM 簡潔方式
      toast.success(t('pages.subscription.subscription_success'));

      // 強制刷新訂閱數據和帳單歷史，確保頁面間資料同步
      subscriptionStore.fetchAllSubscriptions(true);
      subscriptionStore.fetchSystemInvoices();

      onClose();

      // 觸發滾動
      if (onPaymentSuccess) {
        onPaymentSuccess();
      }

    } catch (err: any) {
      let errorMessage = t('common.status.error');

      // 根據錯誤狀態碼提供對應的翻譯訊息
      if (err?.response?.status === 404) {
        errorMessage = t('errors.api.subscription_not_found');
      } else if (err?.response?.status === 403) {
        errorMessage = t('errors.api.access_denied');
      } else if (err?.response?.status === 500) {
        errorMessage = t('errors.api.server_error');
      } else if (err?.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err?.message && !err.message.includes('Request failed with status code')) {
        errorMessage = err.message;
      }

      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <DialogShell onClose={onClose} closeDisabled={loading}>
      <div className="px-6 pt-6 pb-5">
        <h3 className="text-lg font-semibold text-[var(--text-primary)]">
          {t('pages.checkout.confirm_order')}
        </h3>

        {/* 方案與金額（千分位＋等寬數字，與價格卡片一致） */}
        <div className="mt-4 pb-4 border-b border-[var(--border-c)]">
          <p className="text-sm text-[var(--text-secondary)]">{getPlanName(plan.id)}</p>
          <p className="mt-1 text-2xl font-semibold tabular-nums text-[var(--text-primary)]">
            {currency}{plan.price.toLocaleString()}
            <span className="text-sm font-normal text-[var(--text-secondary)] ml-2">
              {plan.price > 0 ? t('pages.checkout.per_month') : ''}
            </span>
          </p>
        </div>

        {/* 包含內容：主題色勾勾清單（不用色塊） */}
        <div className="mt-4">
          <h4 className="text-sm font-medium text-[var(--text-primary)] mb-2">
            {t('pages.checkout.whats_included')}
          </h4>
          <ul className="space-y-1.5">
            {getPlanFeatures(plan.id).map((feature, index) => (
              <li key={index} className="flex items-start text-sm text-[var(--text-secondary)]">
                <CheckmarkIcon className="w-4 h-4 text-[var(--accent)] mr-2 mt-0.5 flex-shrink-0" />
                {feature}
              </li>
            ))}
          </ul>
        </div>
      </div>

      <DialogFooter>
        <button
          type="button"
          onClick={onClose}
          disabled={loading}
          className={dialogButtonClass.cancel}
        >
          {t('common.actions.cancel')}
        </button>
        <LoadingButton
          type="button"
          onClick={handleConfirmPayment}
          loading={loading}
          // portal 主色按鈕統一用 --accent-fill（見 docs/介面優化.md R10）
          className="!bg-[var(--accent-fill)] !text-[var(--accent-on)] hover:!opacity-90"
          customText={{
            create: t('pages.checkout.confirm_payment'),
            createLoading: t('common.status.processing')
          }}
        />
      </DialogFooter>
    </DialogShell>
  );
};

export default CheckoutModal;